import { day } from '~/astra'
import { baseClient } from '~/clients/api-client'
import { AccessTokenResponse } from '~/clients/derived-api-types'

import { isNullish } from './guards'

type AccessToken = AccessTokenResponse | undefined

type RefreshAccessTokenArgs = {
    currentAccessToken: AccessToken
    callback: (newAccessToken: AccessTokenResponse) => void
    onSessionExpired: () => void
}

const margin_s = 60

let refreshTokenTimeout: NodeJS.Timeout | undefined
let cancelled = true
let refreshController: AbortController | undefined
let inFlightRefresh: ReturnType<typeof requestAccessToken> | undefined

function requestAccessToken() {
    return baseClient.GET('/oauth/token')
}

/**
 * Renews the access token, sharing a single request between callers that overlap.
 *
 * The refresh credential is rotated by the server when it is used, so a second request sent before the first
 * response has replaced the credential would present a spent one and be treated as an expired session. Callers
 * cannot cancel the shared request; they should discard the result themselves once they no longer care.
 *
 * This function does not take an abort signal, as we don't want a single caller cancelling the shared request.
 */
export function fetchAccessToken() {
    inFlightRefresh ??= requestAccessToken().finally(() => {
        inFlightRefresh = undefined
    })
    return inFlightRefresh
}

export function cancelRefreshingAccessToken() {
    cancelled = true
    refreshController?.abort()
    refreshController = undefined
    if (refreshTokenTimeout) {
        clearTimeout(refreshTokenTimeout)
        refreshTokenTimeout = undefined
    }
}

function refreshAccessTokenRepeatedly(args: RefreshAccessTokenArgs, retryDelayMs?: number): void {
    const { currentAccessToken, callback, onSessionExpired } = args
    clearTimeout(refreshTokenTimeout)
    refreshTokenTimeout = undefined

    if (cancelled) {
        return
    }

    if (isNullish(currentAccessToken?.token_exp)) {
        // The accessToken doesn't need to be refreshed
        return
    }

    const remainingLifetime_s = currentAccessToken.token_exp - day().unix()
    // Leave room for retries without immediately renewing short-lived tokens.
    const refreshMargin_s = Math.min(margin_s, remainingLifetime_s / 2)
    const delay_s = Math.max(1, remainingLifetime_s - refreshMargin_s)

    refreshTokenTimeout = setTimeout(
        async () => {
            const controller = new AbortController()
            refreshController = controller
            try {
                const { data: newAccessToken, error, response } = await fetchAccessToken()
                if (cancelled || controller.signal.aborted) return
                if (response.status === 401 && error?.code === 'SESSION_EXPIRED') {
                    cancelRefreshingAccessToken()
                    onSessionExpired()
                    return
                }
                if (!newAccessToken) {
                    refreshAccessTokenRepeatedly(args, 5000)
                    return
                }
                if (currentAccessToken.access_token !== newAccessToken.access_token) {
                    callback(newAccessToken)
                }
                refreshAccessTokenRepeatedly({ ...args, currentAccessToken: newAccessToken })
            } catch {
                if (cancelled || controller.signal.aborted) return
                refreshAccessTokenRepeatedly(args, 5000)
            }
        },
        retryDelayMs ?? delay_s * 1000
    )
}

export function scheduleRefreshingAccessToken(args: RefreshAccessTokenArgs): void {
    cancelRefreshingAccessToken()
    cancelled = false
    refreshAccessTokenRepeatedly(args)
}
